<template>
  <div class="data-processing">
    <v-container fluid class="px-6 py-4">
      <!-- Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="header-text">
            <h1 class="page-title">
              <v-icon large class="title-icon mr-3">mdi-cog</v-icon>
              CRM Data Processing
            </h1>
            <p class="page-subtitle">
              Extract and process data from various sources for CRM integration
            </p>
          </div>
          <v-btn
            large
            color="primary"
            class="create-btn"
            @click="showCreateDialog = true"
            elevation="2"
          >
            <v-icon left>mdi-plus</v-icon>
            New Processing Task
          </v-btn>
        </div>
      </div>

      <!-- Filters -->
      <v-card class="filters-card mb-6" elevation="2">
        <v-card-text class="pa-4">
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.status"
                :items="statusOptions"
                label="Status"
                clearable
                dense
                outlined
                @change="fetchTasks"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.source_type"
                :items="sourceTypeOptions"
                label="Source Type"
                clearable
                dense
                outlined
                @change="fetchTasks"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.crm_platform"
                :items="crmPlatformOptions"
                label="CRM Platform"
                clearable
                dense
                outlined
                @change="fetchTasks"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filters.search"
                label="Search"
                clearable
                dense
                outlined
                append-icon="mdi-magnify"
                @input="debouncedSearch"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Tasks Table -->
      <v-card elevation="4">
        <v-data-table
          :headers="headers"
          :items="tasks"
          :loading="isLoading"
          :server-items-length="totalItems"
          :options.sync="tableOptions"
          @update:options="fetchTasks"
          class="elevation-0"
        >
          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              :color="getStatusColor(item.status)"
              dark
              small
              label
            >
              {{ formatStatus(item.status) }}
            </v-chip>
          </template>

          <template v-slot:[`item.source_type`]="{ item }">
            <v-chip
              color="blue"
              text-color="white"
              small
              label
            >
              {{ formatSourceType(item.source_type) }}
            </v-chip>
          </template>

          <template v-slot:[`item.data_quality_score`]="{ item }">
            <div class="quality-score">
              <v-progress-linear
                :value="item.data_quality_score || 0"
                :color="getQualityColor(item.data_quality_score)"
                height="6"
                rounded
                class="mb-1"
              ></v-progress-linear>
              <span class="score-text">{{ Math.round(item.data_quality_score || 0) }}%</span>
            </div>
          </template>

          <template v-slot:[`item.created_at`]="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              icon
              small
              color="primary"
              @click="viewTask(item)"
            >
              <v-icon>mdi-eye</v-icon>
            </v-btn>
            <v-btn
              v-if="item.status === 'pending'"
              icon
              small
              color="success"
              @click="startProcessing(item)"
            >
              <v-icon>mdi-play</v-icon>
            </v-btn>
            <v-btn
              v-if="item.status === 'processed'"
              icon
              small
              color="warning"
              @click="applyToCrm(item)"
            >
              <v-icon>mdi-upload</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>

      <!-- Create Task Dialog -->
      <v-dialog v-model="showCreateDialog" max-width="800px">
        <v-card>
          <v-card-title class="headline">Create New Processing Task</v-card-title>
          <v-card-text>
            <v-form ref="createForm" v-model="formValid">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newTask.processing_name"
                    label="Task Name"
                    :rules="[v => !!v || 'Task name is required']"
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="newTask.source_type"
                    :items="sourceTypeOptions"
                    label="Source Type"
                    :rules="[v => !!v || 'Source type is required']"
                    required
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="newTask.crm_platform"
                    :items="crmPlatformOptions"
                    label="CRM Platform"
                    :rules="[v => !!v || 'CRM platform is required']"
                    required
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="newTask.processing_priority"
                    :items="priorityOptions"
                    label="Priority"
                  ></v-select>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="newTask.source_content"
                    label="Source Content"
                    placeholder="Paste your call transcript, email content, meeting notes, or other source data here..."
                    rows="6"
                    :rules="[v => !!v || 'Source content is required']"
                    required
                  ></v-textarea>
                </v-col>
                <v-col cols="12">
                  <v-checkbox
                    v-model="newTask.auto_apply_updates"
                    label="Auto-apply updates to CRM if quality score > 80%"
                  ></v-checkbox>
                </v-col>
                <v-col cols="12">
                  <v-checkbox
                    v-model="newTask.require_manual_review"
                    label="Always require manual review before applying changes"
                  ></v-checkbox>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="closeCreateDialog">Cancel</v-btn>
            <v-btn 
              color="primary" 
              :loading="isCreating"
              :disabled="!formValid"
              @click="createTask"
            >
              Create Task
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- View Task Dialog -->
      <v-dialog v-model="showViewDialog" max-width="900px">
        <v-card v-if="selectedTask">
          <v-card-title class="headline">
            Task Details: {{ selectedTask.processing_name }}
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <strong>Status:</strong>
                <v-chip
                  :color="getStatusColor(selectedTask.status)"
                  dark
                  small
                  label
                  class="ml-2"
                >
                  {{ formatStatus(selectedTask.status) }}
                </v-chip>
              </v-col>
              <v-col cols="12" md="6">
                <strong>Quality Score:</strong> {{ Math.round(selectedTask.data_quality_score || 0) }}%
              </v-col>
              <v-col cols="12" md="6">
                <strong>Source Type:</strong> {{ formatSourceType(selectedTask.source_type) }}
              </v-col>
              <v-col cols="12" md="6">
                <strong>CRM Platform:</strong> {{ formatCrmPlatform(selectedTask.crm_platform) }}
              </v-col>
            </v-row>
            
            <v-divider class="my-4"></v-divider>
            
            <div v-if="selectedTask.extracted_data" class="mb-4">
              <h3 class="mb-2">Extracted Data</h3>
              <v-card outlined>
                <v-card-text>
                  <pre class="extracted-data">{{ JSON.stringify(selectedTask.extracted_data, null, 2) }}</pre>
                </v-card-text>
              </v-card>
            </div>
            
            <div v-if="selectedTask.ai_analysis" class="mb-4">
              <h3 class="mb-2">AI Analysis</h3>
              <v-card outlined>
                <v-card-text>
                  {{ selectedTask.ai_analysis }}
                </v-card-text>
              </v-card>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="closeViewDialog">Close</v-btn>
            <v-btn 
              v-if="selectedTask.status === 'pending'"
              color="success" 
              @click="startProcessing(selectedTask)"
            >
              Start Processing
            </v-btn>
            <v-btn 
              v-if="selectedTask.status === 'processed'"
              color="warning" 
              @click="applyToCrm(selectedTask)"
            >
              Apply to CRM
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import axios from "@/axios";
import { debounce } from "lodash";

export default {
  name: "CRMGenieDataProcessing",
  data() {
    return {
      isLoading: false,
      isCreating: false,
      formValid: false,
      showCreateDialog: false,
      showViewDialog: false,
      selectedTask: null,
      tasks: [],
      totalItems: 0,
      tableOptions: {},
      filters: {
        status: null,
        source_type: null,
        crm_platform: null,
        search: null,
      },
      newTask: {
        processing_name: '',
        source_type: null,
        crm_platform: null,
        source_content: '',
        processing_priority: 'medium',
        auto_apply_updates: false,
        require_manual_review: true,
      },
      headers: [
        { text: 'Task', value: 'processing_name', sortable: true },
        { text: 'Source', value: 'source_type', sortable: false },
        { text: 'CRM Platform', value: 'crm_platform', sortable: false },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Quality Score', value: 'data_quality_score', sortable: true },
        { text: 'Created', value: 'created_at', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
      ],
      statusOptions: [
        { text: 'Pending', value: 'pending' },
        { text: 'Processing', value: 'processing' },
        { text: 'Processed', value: 'processed' },
        { text: 'Review Required', value: 'review_required' },
        { text: 'Applied', value: 'applied' },
        { text: 'Failed', value: 'failed' },
      ],
      sourceTypeOptions: [
        { text: 'Call Transcript', value: 'call_transcript' },
        { text: 'Email', value: 'email' },
        { text: 'Meeting Notes', value: 'meeting_notes' },
        { text: 'CRM Export', value: 'crm_export' },
        { text: 'Manual Entry', value: 'manual_entry' },
      ],
      crmPlatformOptions: [
        { text: 'Salesforce', value: 'salesforce' },
        { text: 'HubSpot', value: 'hubspot' },
        { text: 'Pipedrive', value: 'pipedrive' },
        { text: 'Zoho', value: 'zoho' },
        { text: 'Custom', value: 'custom' },
        { text: 'Other', value: 'other' },
      ],
      priorityOptions: [
        { text: 'Low', value: 'low' },
        { text: 'Medium', value: 'medium' },
        { text: 'High', value: 'high' },
        { text: 'Urgent', value: 'urgent' },
      ],
    };
  },
  computed: {
    debouncedSearch() {
      return debounce(this.fetchTasks, 300);
    },
  },
  watch: {
    tableOptions: {
      handler() {
        this.fetchTasks();
      },
      deep: true,
    },
  },
  async mounted() {
    await this.fetchTasks();
  },
  methods: {
    async fetchTasks() {
      try {
        this.isLoading = true;
        const { page = 1, itemsPerPage = 10, sortBy = [], sortDesc = [] } = this.tableOptions;
        
        const params = {
          page,
          per_page: itemsPerPage,
          ...this.filters,
        };

        // Handle sorting
        if (sortBy.length > 0) {
          params.sort = sortBy[0];
          params.order = sortDesc[0] ? 'desc' : 'asc';
        }

        // Remove empty filters
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key];
          }
        });

        const response = await axios.get('crm-data-processing', { params });
        
        if (response.data.success) {
          this.tasks = response.data.data;
          this.totalItems = response.data.pagination?.total || 0;
        }
      } catch (error) {
        console.error('Error fetching tasks:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to load processing tasks. Please try again.']
        });
      } finally {
        this.isLoading = false;
      }
    },

    async createTask() {
      if (!this.$refs.createForm.validate()) return;

      try {
        this.isCreating = true;
        const response = await axios.post('crm-data-processing', this.newTask);
        
        if (response.data.success) {
          this.$store.dispatch('errors/setSuccess', 'Processing task created successfully');
          this.closeCreateDialog();
          await this.fetchTasks();
        }
      } catch (error) {
        console.error('Error creating task:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to create processing task. Please try again.']
        });
      } finally {
        this.isCreating = false;
      }
    },

    async startProcessing(task) {
      try {
        this.isLoading = true;
        const response = await axios.post(`crm-data-processing/${task.id}/start-processing`);
        
        if (response.data.success) {
          this.$store.dispatch('errors/setSuccess', 'Processing started successfully');
          await this.fetchTasks();
          this.closeViewDialog();
        }
      } catch (error) {
        console.error('Error starting processing:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to start processing. Please try again.']
        });
      } finally {
        this.isLoading = false;
      }
    },

    async applyToCrm(task) {
      try {
        this.isLoading = true;
        const response = await axios.post(`crm-data-processing/${task.id}/apply-to-crm`);
        
        if (response.data.success) {
          this.$store.dispatch('errors/setSuccess', 'Successfully applied to CRM');
          await this.fetchTasks();
          this.closeViewDialog();
        }
      } catch (error) {
        console.error('Error applying to CRM:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to apply to CRM. Please try again.']
        });
      } finally {
        this.isLoading = false;
      }
    },

    viewTask(task) {
      this.selectedTask = task;
      this.showViewDialog = true;
    },

    closeCreateDialog() {
      this.showCreateDialog = false;
      this.newTask = {
        processing_name: '',
        source_type: null,
        crm_platform: null,
        source_content: '',
        processing_priority: 'medium',
        auto_apply_updates: false,
        require_manual_review: true,
      };
      this.$refs.createForm?.resetValidation();
    },

    closeViewDialog() {
      this.showViewDialog = false;
      this.selectedTask = null;
    },

    getStatusColor(status) {
      const colors = {
        'pending': 'orange',
        'processing': 'blue',
        'processed': 'green',
        'review_required': 'amber',
        'applied': 'purple',
        'failed': 'red',
        'cancelled': 'grey'
      };
      return colors[status] || 'grey';
    },

    getQualityColor(score) {
      if (score >= 80) return 'green';
      if (score >= 60) return 'orange';
      return 'red';
    },

    formatStatus(status) {
      return status.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    },

    formatSourceType(sourceType) {
      const types = {
        'call_transcript': 'Call Transcript',
        'email': 'Email',
        'meeting_notes': 'Meeting Notes',
        'crm_export': 'CRM Export',
        'manual_entry': 'Manual Entry'
      };
      return types[sourceType] || sourceType;
    },

    formatCrmPlatform(platform) {
      const platforms = {
        'salesforce': 'Salesforce',
        'hubspot': 'HubSpot',
        'pipedrive': 'Pipedrive',
        'zoho': 'Zoho',
        'custom': 'Custom',
        'other': 'Other'
      };
      return platforms[platform] || platform;
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
  },
};
</script>

<style scoped>
.data-processing {
  min-height: calc(100vh - 64px);
}

.page-header {
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.title-icon {
  color: #667eea;
}

.page-subtitle {
  font-size: 16px;
  color: #64748b;
  margin: 8px 0 0 0;
  font-weight: 400;
}

.create-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  height: 48px !important;
}

.filters-card {
  border-radius: 12px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.quality-score {
  min-width: 120px;
}

.score-text {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.extracted-data {
  font-size: 12px;
  max-height: 300px;
  overflow-y: auto;
  background: #f8f9fa;
  padding: 12px;
  border-radius: 4px;
  white-space: pre-wrap;
  word-wrap: break-word;
}

@media (max-width: 960px) {
  .header-content {
    flex-direction: column;
    text-align: center;
  }
  
  .page-title {
    font-size: 28px;
    justify-content: center;
  }
  
  .page-header {
    padding: 24px 20px;
  }
}

@media (max-width: 600px) {
  .page-header {
    padding: 20px 16px;
  }
  
  .page-title {
    font-size: 24px;
    flex-direction: column;
    gap: 8px;
  }
  
  .create-btn {
    width: 100%;
  }
}
</style>